import React from 'react';
import {
  Typography,
  Grid,
  Box,
  Card,
  Button,
  Divider,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
  TableCell
} from '@mui/material';
import { format, isValid } from "date-fns";
import { useAppContext } from '../AppContext';

const OrderSummaryReview = ({ 
  orderDetails, 
  onEdit, 
  onConfirm, 
  onCancel,
  cartItems
}) => {
  const { t } = useAppContext();
  const formatToMyanmarCurrency = (number) => {
    return new Intl.NumberFormat('my-MM').format(number) + ' MMK';
  };

  return (
    <Box>
      <Typography variant="h4" mt={3} gutterBottom>
        {t("Order Summary Review")}
      </Typography>
      
      <Card variant="outlined" sx={{ padding: 3, marginBottom: 3 }}>
        <Grid container spacing={2}>
          {/* Customer Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6">{t("Customer Information")}</Typography>
            <Typography>{t("Name")}: {orderDetails.customerName}</Typography>
            <Typography>{t("Phone")}: {orderDetails.phoneNumber}</Typography>
            {orderDetails.deliveryAddress && (
              <Typography>{t("Address")}: {orderDetails.deliveryAddress}</Typography>
            )}
          </Grid>

          {/* Order Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6">{t("Order Details")}</Typography>
            <Typography>{t("Fulfillment Method")}: {orderDetails.fulfillmentMethod}</Typography>
            <Typography>
               {t("Fulfillment Date")}: {format(orderDetails.fulfillmentDate,"dd-MM-yyyy")}
            </Typography>
            <Typography>
              {t("Fulfillment Time")}: {orderDetails.fulfillmentTime?.toLocaleTimeString()}
            </Typography>
            <Typography>
              {t("Inscription")}: {orderDetails.inscription}
            </Typography>
            <Typography>
              {t("Cashier")}: {orderDetails.cashierName}
            </Typography>
          </Grid>

          {/* Cart Items */}  
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t("Cart Items")}
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Name")}</TableCell>
                    <TableCell align="right">{t("Qty")}</TableCell>
                    <TableCell align="left">{t("Variant")}</TableCell>
                    <TableCell align="right">{t("Price")}</TableCell>
                    <TableCell align="right">{t("Amount")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartItems.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="left"><strong>{item.selectedVariant}</strong></TableCell>
                      <TableCell align="right">{formatToMyanmarCurrency(item.price + item.variantPrice)}</TableCell>
                      <TableCell align="right">{formatToMyanmarCurrency(item.totalPrice)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Order Images */}
          <Grid item xs={12}>
            {orderDetails.orderImagePreviews && orderDetails.orderImagePreviews.length > 0 && (
              <Box mt={2}>
                {orderDetails.orderImagePreviews.map((image, index) => (
                  <Box key={index} mt={2} mr={2} sx={{ position: 'relative', display: 'inline-block' }}>
                    <Typography variant="body2">
                      {image.name}
                    </Typography>
                    <img 
                      src={image} 
                      alt=""
                      style={{ width: '200px', height: '200px', objectFit: 'contain' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Financial Summary */}
          <Grid item xs={12}>
            <Typography variant="h6">{t("Financial Summary")}</Typography>
            <Divider sx={{ marginBottom: 2 }} />
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography>{t("Subtotal")}:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography>{formatToMyanmarCurrency(orderDetails.subtotal)}</Typography>
              </Grid>
              
              <Grid item xs={6}>
                <Typography>{t("Tax")}:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography>{formatToMyanmarCurrency(orderDetails.tax)}</Typography>
              </Grid>
              
              {orderDetails.deliveryFeeAmount > 0 && (
                <>
                  <Grid item xs={6}>
                    <Typography>{t("Delivery Charge")}:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography>{formatToMyanmarCurrency(orderDetails.deliveryFeeAmount)}</Typography>
                  </Grid>
                </>
              )}
              
              <Grid item xs={6}>
                <Typography>{t("Discount")}:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography>{formatToMyanmarCurrency(orderDetails.discount || 0)}</Typography>
              </Grid>
              
              <Divider sx={{ width: '100%', margin: '16px 0' }} />
              
              <Grid item xs={6}>
                <Typography variant="h6">{t("Total")}:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="h6">
                  {formatToMyanmarCurrency(orderDetails.total)}
                </Typography>
              </Grid>

              {/* Updated Payment Section */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>{t("Payments")}:</Typography>
                {orderDetails.payments && orderDetails.payments.map((payment, index) => (
                  <Grid container key={index} spacing={1}>
                    <Grid item xs={6}>
                      <Typography>{t(payment.method)}:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{ textAlign: 'right' }}>
                      <Typography>{formatToMyanmarCurrency(payment.amount)}</Typography>
                    </Grid>
                  </Grid>
                ))}
              </Grid>

              <Grid item xs={6}>
                <Typography variant="h6">{t("Total Paid")}:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="h6">
                  {formatToMyanmarCurrency(orderDetails.payments?.reduce((sum, p) => sum + p.amount, 0) || 0)}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="h6">{t("Balance")}:</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="h6">
                  {formatToMyanmarCurrency(orderDetails.remainingBalance)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, marginBottom: 3 }}>
        <Button 
          variant="outlined" 
          color="secondary" 
          onClick={onEdit}
        >
          {t("Edit Order")}
        </Button>
        <Box>
          <Button 
            variant="outlined" 
            color="error" 
            onClick={onCancel} 
            sx={{ marginRight: 2 }}
          >
            {t("Cancel Order")}
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={onConfirm}
          >
            {t("Confirm Order")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default OrderSummaryReview;
